import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { differsAtCentPrecision } from '../validation-field-key.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  areRole8YearsConsecutive,
  buildRole8ColumnKey,
  buildRole19EquityKey,
  extractRole8SchemaTaxonomy,
  getRole8EquityCrossSheetTargets,
  isRole8CalculatedField,
  isRole8Empty,
  isRole8MonetaryItem,
  pickRole19Schema,
  pickRole8Schema,
  role8AccountingStandardToTaxonomy,
  Role8Schema,
} from './role8-statement-of-financial-position.rules';
import {
  readSlice,
  resolveDimensionVisibility,
} from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';
import {
  accountingStandardToXbrlPrefix,
  applyLinkedInheritance,
  buildEqualitySnapshot,
  calculator,
  hasCalculatedFieldMismatch,
  isResolvedAccountingStandard,
} from './calc-diff.helper';
import {
  buildSocieColumnContextFromDimensionVisibility,
  recomputeReferencedSheets,
} from '../cross-sheet-recompute.util';
import { ROLE9_SUBCLASSIFICATION_LINK_ROLE } from './link-roles';
import { isMandatoryForValidation } from '../../../utils/required-field.util';

/**
 * Pure validator for Role 8 (Statement of Financial Position).
 *
 * Mirrors the editor's role8-210000-statement-of-financial-position.component.ts
 * `evaluateSheetStatus()` surface — i.e. which rows resolve to the 'Failed'
 * mapping status. The component also computes per-row 'Validated*' /
 * 'Edited' / 'Validated' states from API-mismatch baselines, edit-mode
 * tracking, and decimal-format checks, but those map to
 * `hasSuggestedChanges` / display affordances and never flow through
 * `updateSheetLevelStatus({ hasErrors })`. We restrict the pure path to the
 * deterministic Failed drivers so the parity-check harness can compare
 * like-for-like.
 *
 * Failed drivers actually mirrored here:
 *
 *  1. **Static required-field check** (`control.required === true`). The
 *     editor's validateMandatoryFields() walks every visible
 *     (dimension × period) cell of every required MonetaryItemType control
 *     and flags it when the value is null. We mirror that, broadening the
 *     "empty" definition to undefined / '' to tolerate orchestrator payloads
 *     where the cell was never persisted (same posture as roles 9, 15, 23).
 *     The Map populated by validateMandatoryFields feeds
 *     `mandatoryFieldErrors` which is then probed by getRowValidationStatus()
 *     to decide Failed.
 *
 *  2. **Schema-declared cross-sheet equality** (column-aware branch of
 *     EqualityValidationService.validateAllEqualityRules). For role 8 today
 *     this drives:
 *      - `ssmt:CashAndBankBalances` / `ifrs-full:CashAndCashEquivalents`
 *        comparison against role 18 (Cash Flows Indirect)
 *      - `ifrs-full:Assets` ↔ `ifrs-full:EquityAndLiabilities` self-check
 *        (the "balance sheet balances" tie-out)
 *     Both schemas declare the rules with `sheet`/`field` only (no
 *     `operation: sum`), so we walk the standard-equality branch. Mismatches
 *     beyond cent precision (any ≥ 0.01 difference) flip Failed.
 *
 *  3. **Cross-sheet equity-by-year reconciliation against role 19**
 *     (validateCrossSheetEquityByYear). Compares Role 8's `:Equity` total
 *     against Role 19's `:Equity:End` total per (dimension × period) column,
 *     gated by:
 *      - The rule existing on the SOCIE schema's `:Equity:End` control.
 *      - The current-year-start vs previous-year-end dates being
 *        consecutive (current === previous + 1 day).
 *     Mismatch at cent precision (any ≥ 0.01 difference) flips Failed.
 *
 * Drivers intentionally NOT mirrored (would not change `hasErrors`):
 *
 *  - API-mismatch warnings ("does not match the extracted sum") populated by
 *    validateAgainstApiData → these resolve to Validated*, not Failed.
 *  - Edited rows / edit-mode tracking → resolve to Edited, not Failed.
 *  - Form control invalid + dirty/touched checks → orchestrator runs over
 *    persisted payloads where dirty/touched are not available, and the
 *    component's getValidators() attaches no validators (mirrors the
 *    component's "We don't add Validators.required for now" comment), so
 *    this branch is structurally a no-op in pure form.
 *  - Calculated-totals computation → the editor populates calculated fields
 *    via CalculationService at form-build time and persists the results.
 *    The orchestrator reads the post-calculation values from sheetData
 *    directly; we do NOT need to replay the calculations to evaluate
 *    hasErrors (only the equality result matters, and the editor's equality
 *    check ALSO runs against the post-calculation persisted values).
 *
 * ---- Cross-sheet dependency notes ----------------------------------------
 *
 *  - `filingPrerequisites` (Role 0) — required for column visibility AND for
 *    the consecutive-year date gate on the Equity-by-year rule. Tolerated
 *    absent: visibility defaults to "all four columns visible", consecutive
 *    check returns false (rule silently skipped — same posture as the
 *    editor's `if (!deiData) return errors`).
 *  - `statementOfCashFlowsIndirectMethod` (Role 18) — required for the
 *    Cash equality check. Tolerated absent: source value resolves to null
 *    and the rule short-circuits at the `rawCompareValue === null` skip
 *    path, matching the editor's pre-load behaviour.
 *  - `statementOfChangesInEquity` (Role 19) — required for the Equity
 *    cross-sheet by-year check. Tolerated absent: rule silently skipped via
 *    the editor's `if (!sfpData || !socieData) return errors`. Note: the
 *    SchemaDependencyService cycle (role 8 ↔ role 19) is intentional — both
 *    sheets publish equity totals that have to agree.
 *  - `subClassificationOfAssetsLiabilitiesEquity` (Role 9) — feeds Role 8
 *    via `linkTo` inheritance at form-build time. By the time we read
 *    sheetData, the inherited values are already part of role 8's persisted
 *    payload, so the validator does NOT need to read role 9's slice
 *    directly (matches how validateAllEqualityRules already operates over
 *    the merged exportAllData snapshot).
 */

interface CrossSheetReader {
  read(sheet: string): Record<string, any> | undefined;
}

function makeCrossSheetReader(
  context: SheetValidationContext
): CrossSheetReader {
  const bundle = context.crossSheetData;
  return {
    read(sheet: string) {
      if (!bundle) return undefined;
      return bundle.get(sheet as Parameters<typeof bundle.get>[0]) as
        | Record<string, any>
        | undefined;
    },
  };
}

/**
 * Coerce a raw cell value into a number for equality comparison. Mirrors
 * the cleanup performed by validation-field-value.util's getFieldValueForColumn:
 * null / undefined / '' return null; non-numeric strings return null.
 */
function coerceNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Cross-sheet field lookup with the same calculated/readonly fallback the
 * editor uses for `dualColumn` sheets. When the source sheet has cached
 * schema info marking the field as calculated/readonly, a missing cell
 * resolves to 0 (matches "Non-editable fields display 0 when null"). The
 * pure path does not have generic access to other sheets' schemas, so the
 * fallback is keyed on whether we know the field is the SFP's own (via
 * the local schema) or the SOCIE's (via pickRole19Schema). For role 18
 * (Cash Flows) the fallback is conservative — null when cell is missing —
 * which matches the editor's behaviour when no cached schema exists for
 * the source sheet.
 */
function readCellForColumn(
  payload: Record<string, any> | undefined,
  key: string
): number | null {
  return coerceNumber(payload?.[key]);
}

/**
 * Mirrors validateMandatoryFields(): for every required MonetaryItemType
 * control, every visible (dimension × period) cell must carry a non-null
 * value. Null → mandatoryFieldErrors gets the qname → row resolves to
 * Failed. Returns true on first missing cell — short-circuit because we
 * only care about hasErrors true/false (matches evaluateSheetStatus's
 * break on first Failed row).
 */
function checkRequiredFieldErrors(
  schema: Role8Schema,
  sheetData: Record<string, any>,
  visibleColumns: ReadonlyArray<{
    dimension: 'Consolidated' | 'Separate';
    period: 'CurrentYear' | 'PreviousYear';
    visible: boolean;
  }>
): boolean {
  for (const control of schema.controls) {
    if (!isRole8MonetaryItem(control)) continue;
    // `isMandatoryForValidation` — NOT `isControlMandatory`. The former excludes
    // computed controls, so a calculated total flagged mandatory to light its
    // `*` cannot also block submit on a null value. See the two Developer Notes
    // on that function for why this is a shared predicate rather than a schema
    // flag, and for the role 15 failure it prevents.
    if (!isMandatoryForValidation(control)) continue;

    for (const column of visibleColumns) {
      if (!column.visible) continue;
      const key = buildRole8ColumnKey(
        control.qname,
        column.dimension,
        column.period
      );
      if (isRole8Empty(sheetData[key])) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Mirrors the column-aware branch of validateAllEqualityRules: for each
 * control with `validation.equality` blocks, for each rule, compare
 * sheetData[control.qname][col] against
 * crossSheetData[rule.sheet][rule.field][col] for every visible column.
 *
 * Only the standard-equality branch is exercised here because both role 8
 * MFRS and MPERS schemas declare equality blocks WITHOUT `operation: 'sum'`.
 * If a future schema edit introduces a sum operation, expand this function
 * (the helper signature is generic enough that the additional branch slots
 * in cleanly — flag in the latent-bugs ledger if added).
 */
function checkEqualityErrors(
  schema: Role8Schema,
  sheetData: Record<string, any>,
  reader: CrossSheetReader,
  visibleColumns: ReadonlyArray<{
    dimension: 'Consolidated' | 'Separate';
    period: 'CurrentYear' | 'PreviousYear';
    visible: boolean;
  }>,
  taxonomy: 'MFRS' | 'MPERS'
): boolean {
  for (const control of schema.controls) {
    const rules = control.validation?.equality;
    if (!rules || rules.length === 0) continue;

    // Pure path mirrors the editor: standard equality (one rule = one
    // pairwise compare). We deliberately skip sum-style equality (none
    // declared today; see header comment).
    const isSum = rules.every((rule) => rule.operation === 'sum');
    if (isSum) continue;

    for (const rule of rules) {
      if (
        rule.taxonomy &&
        rule.taxonomy.toUpperCase() !== taxonomy.toUpperCase()
      ) {
        continue;
      }

      // Source sheet data: when comparing against the same sheet, use the
      // local sheetData; otherwise pull the slice from the cross-sheet
      // bundle (tolerates absence).
      const sourcePayload =
        rule.sheet === SHEET_KEYS.statementOfFinancialPosition
          ? sheetData
          : reader.read(rule.sheet);

      for (const column of visibleColumns) {
        if (!column.visible) continue;

        const localKey = buildRole8ColumnKey(
          control.qname,
          column.dimension,
          column.period
        );
        const sourceKey = buildRole8ColumnKey(
          rule.field,
          column.dimension,
          column.period
        );

        const rawLocal = sheetData[localKey];
        const rawSource = sourcePayload?.[sourceKey];

        // Editor: skip when both null (nothing to compare).
        if (isRole8Empty(rawLocal) && isRole8Empty(rawSource)) continue;

        // Editor's conditional skip flags — preserved for parity even
        // though no rule on role 8 sets them today.
        if (rule.skipIfSourceNull && isRole8Empty(rawLocal)) continue;
        if (rule.skipIfTargetNull && isRole8Empty(rawSource)) continue;

        // Editor: treat nulls as 0 once we've decided the column is in
        // play. NaN side gets neutralised the same way.
        const localNum = coerceNumber(rawLocal) ?? 0;
        const sourceNum = coerceNumber(rawSource) ?? 0;

        // Round to cents, flag any difference (≥ 0.01) — a one-cent mismatch
        // must be reported (the old `> tolerance` only flagged ≥ 0.02).
        if (differsAtCentPrecision(localNum, sourceNum)) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Cross-sheet equity reconciliation — replays the SOCIE (role 19) schema's
 * `crossSheetEquityByYear` rule on Role 8's `:Equity` against Role 19's
 * recomputed `:Equity:End:EquityMember` (the equity TOTAL column) per visible
 * column. Mismatch at cent precision (any ≥ 0.01 difference) → Failed.
 *
 * Period scope (AS-1076) — handled per-column in the loop:
 *   - CURRENT YEAR: compared UNGATED (current-year columns always render, incl.
 *     first-time filers and non-consecutive FY boundaries).
 *   - PREVIOUS YEAR: compared only on a consecutive FY boundary (and, via column
 *     visibility, only for subsequent filings — PreviousYear columns are hidden
 *     for first-time filers).
 * Both Consolidated and Separate. Tolerates absence of the SOCIE slice / DEI
 * dates — a missing/non-consecutive boundary simply gates out the Previous-Year
 * leg while Current Year still compares.
 */
function checkCrossSheetEquityByYear(
  sheetData: Record<string, any>,
  reader: CrossSheetReader,
  context: SheetValidationContext,
  visibleColumns: ReadonlyArray<{
    dimension: 'Consolidated' | 'Separate';
    period: 'CurrentYear' | 'PreviousYear';
    visible: boolean;
  }>
): boolean {
  const filingPrerequisites = reader.read(SHEET_KEYS.filingPrerequisites);
  // Per-column gate (below), not a blanket early-return: a non-consecutive /
  // missing boundary must still let the Current-Year leg run.
  const consecutive = areRole8YearsConsecutive(filingPrerequisites);

  const rawSocie = reader.read(SHEET_KEYS.statementOfChangesInEquity);
  if (!rawSocie) return false;

  const socieSchema = pickRole19Schema(context.accountingStandard);
  const targets = getRole8EquityCrossSheetTargets(context.accountingStandard);

  // Locate the SOCIE control carrying the rule. The editor probes both
  // sides (SFP and SOCIE) for the rule definition; in practice only the
  // SOCIE schema declares it today, so mirror that primary path.
  const socieControl = socieSchema.controls.find(
    (control) => control.qname === targets.socieEquityEndQname
  );
  const rule = socieControl?.validation?.crossSheetEquityByYear;
  if (!rule) return false;

  // Rule-level taxonomy filter — preserved for parity even though no
  // rule on role 19 sets it today.
  const taxonomy = role8AccountingStandardToTaxonomy(
    context.accountingStandard
  );
  if (rule.taxonomy && rule.taxonomy.toUpperCase() !== taxonomy.toUpperCase()) {
    return false;
  }

  // Recompute the SOCIE `:Equity:End` operand BEFORE comparing — the SAME
  // recipe the mounted role8 editor runs via recomputeReferencedSheets →
  // calculateTotals_610000 (see role8-...component.ts's `reconciliationBag`).
  // The editor reconciles Role-8's recomputed `:Equity` against Role-19's
  // RECOMPUTED `:Equity:End`, never the raw N8N extraction. Reading the raw
  // slice here was a false-negative: when the extracted `Equity:End`
  // coincidentally tied with SFP `:Equity` while the SOCIE roll-forward did NOT
  // reconcile (Perm-24), the headless verdict published a green tick that the
  // editor's red cross never reset — the exact drift PURE_VERDICT_SHEETS is
  // meant to eliminate. The visible-column context is derived from this
  // validator's already-resolved `visibleColumns` (the shared
  // resolveDimensionVisibility registry), so it cannot drift from the SFP-side
  // visibility used by the comparison loop below.
  const socieColumnContext = buildSocieColumnContextFromDimensionVisibility(
    visibleColumns,
    (socieSchema as { equityColumns?: Array<{ qname: string }> }).equityColumns
  );
  // The SOCIE `:Equity:End` operand is recompute-of-latest — never the N8N
  // snapshot (revised global rule).
  const socieData =
    recomputeReferencedSheets(
      { [SHEET_KEYS.statementOfChangesInEquity]: rawSocie },
      [SHEET_KEYS.statementOfChangesInEquity],
      taxonomy,
      () => socieSchema,
      socieColumnContext
    )[SHEET_KEYS.statementOfChangesInEquity] ?? rawSocie;

  const sfpControlIsCalculated = true; // SFP `:Equity` is always calculated.
  const socieControlIsCalculated = isRole8CalculatedField(socieControl);

  for (const column of visibleColumns) {
    if (!column.visible) continue;

    // AS-1076 period scope: Current Year is ungated; Previous Year only on a
    // consecutive FY boundary. (Previous columns are hidden for first-time
    // filers, reproducing the editor's isSubsequentPreparation() gate via
    // visibility.)
    if (column.period === 'PreviousYear' && !consecutive) continue;

    const sfpKey = buildRole8ColumnKey(
      targets.sfpEquityQname,
      column.dimension,
      column.period
    );
    const socieKey = buildRole19EquityKey(
      targets.socieEquityEndQname,
      column.dimension,
      column.period,
      context.accountingStandard
    );

    const sfpRaw = sheetData[sfpKey];
    const socieRaw = socieData[socieKey];

    let sfpNum: number | null;
    if (!isRole8Empty(sfpRaw)) {
      sfpNum = coerceNumber(sfpRaw);
    } else {
      // Editor: calculated/readonly missing → display 0.
      sfpNum = sfpControlIsCalculated ? 0 : null;
    }

    let socieNum: number | null;
    if (!isRole8Empty(socieRaw)) {
      socieNum = coerceNumber(socieRaw);
    } else {
      socieNum = socieControlIsCalculated ? 0 : null;
    }

    // Editor: skip if either side is null/NaN.
    if (sfpNum === null || socieNum === null) continue;
    if (!Number.isFinite(sfpNum) || !Number.isFinite(socieNum)) continue;

    if (differsAtCentPrecision(sfpNum, socieNum)) {
      return true;
    }
  }

  return false;
}

/**
 * Role code Role-8 controls carry in `ui.linkTo.role` when they inherit their
 * value from the Role-9 sub-classification sheet. Mirrors the editor's
 * pullRole9Values() filter (`control.ui.linkTo.role === '210100'`).
 *
 * Single-sourced in `./link-roles.ts` and re-exported here so the mounted
 * cross-sheet recompute util shares the exact same literal without importing
 * this large validator module.
 */
export { ROLE9_SUBCLASSIFICATION_LINK_ROLE };

export const role8StatementOfFinancialPositionValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfFinancialPosition,
  // Tier 4: highest-stakes — cross-sheet reads (filingPrerequisites for
  // visibility + role 18 for cash equality + role 19 for equity-by-year)
  // PLUS calculated totals driven by sum logic. The dual-path parity
  // harness opts this validator into runtime comparison against the
  // mount-based path on every updateSheetLevelStatus emission.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole8Schema(context.accountingStandard);
    const reader = makeCrossSheetReader(context);
    // Column visibility now flows through the shared, config-driven resolver
    // (fed by the central getDimensionVisibilityConfig() registry) so this
    // headless path and the mounted editor's CrossReferenceService.getVisibility()
    // derive the four (dimension × period) cells identically and cannot drift.
    // The resolver applies the same prereq-first, then-filingInfo precedence the
    // role-local resolveRole8ColumnVisibility() helper used to.
    const visibleColumns = resolveDimensionVisibility(
      getDimensionVisibilityConfig(SHEET_KEYS.statementOfFinancialPosition)!,
      {
        filingPrerequisites: readSlice(context.crossSheetData, 'filingPrerequisites'),
        filingInfo: readSlice(context.crossSheetData, 'filingInfo'),
      }
    );

    // Treat as no-op when the schema's taxonomy contradicts the
    // orchestrator's taxonomy — mirrors validateAllEqualityRules' strict
    // mode. By construction pickRole8Schema returns a schema whose
    // taxonomy matches contextTaxonomy, but we keep the guard for
    // unrecognised accountingStandard values (same posture as role 9).
    const contextTaxonomy = role8AccountingStandardToTaxonomy(
      context.accountingStandard
    );
    const schemaTaxonomy = extractRole8SchemaTaxonomy(schema);
    const taxonomiesAgree =
      schemaTaxonomy !== null && schemaTaxonomy === contextTaxonomy;
    // -----------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // Diagnostic logs — kept while we reconfirm that the extraction-vs-calc gap
    // (e.g. ifrs-full:Assets persisted vs recomputed) lands in hasSuggestedChanges
    // and NOT hasErrors. Remove once the values are confirmed across a sample.
    // console.log('[On Role 8 Financial Position | Validation]: Taxonomies Agree: ', taxonomiesAgree, '(context:', contextTaxonomy, ', schema:', schemaTaxonomy, ')');
    // console.log('[On Role 8 Financial Position | Validation]: Field Errors: ', checkRequiredFieldErrors(schema, sheetData, visibleColumns));
    // -----------------
    // Layer 1: required-field check (component's mandatoryFieldErrors).
    // Runs against the persisted payload — the editor validates mandatory
    // presence on the raw control values, not on recomputed totals.
    if (checkRequiredFieldErrors(schema, sheetData, visibleColumns)) {
      return { hasErrors: true, hasSuggestedChanges: false };
    }

    // Build the equality operands the SAME way the mounted editor does, by
    // replaying its two pre-validation transforms on Role-8's leaves:
    //
    //   1. Role-9 inheritance — pullRole9Values() overwrites Role-8's
    //      Role-9-linked leaf line items with Role-9's current sub-classification
    //      values BEFORE anything else. A Role-9 total that disagrees with
    //      Role-8's persisted leaf shifts the recomputed Assets/Equity, which
    //      can break the Assets == EquityAndLiabilities self-balance (and the
    //      cross-sheet equity tie-out). Recomputing from Role-8's persisted
    //      slice alone hid that imbalance — the listing showed a green tick
    //      while opening the sheet (which inherits first) showed a red cross.
    //   2. calculateTotals() — recompute the calculated totals from the (now
    //      inherited) leaves and overlay them, so the equality rules compare
    //      freshly-summed totals rather than the raw N8N extraction.
    //
    // The original `sheetData` is preserved untouched for the Validated* diff
    // below (that asterisk reports "recomputed sum vs N8N extraction", measured
    // against the raw persisted slice — NOT the inherited one).
    //
    // IMPORTANT — recompute Role-9 BEFORE inheriting. The mounted editor never
    // inherits Role-9's RAW slice: filing-form-container recomputes Role-9's
    // totals at load (calculateTotals_210100, "so that Role8 can display them
    // without needing to open Role9 first") and persists them, so pullRole9Values
    // pulls the RECOMPUTED totals. When Role-9 has an internal extracted-vs-sum
    // mismatch (e.g. extracted ShorttermBorrowings 117.60 but its line items sum
    // to 17.04), only the recomputed total breaks Role-8's Assets ==
    // EquityAndLiabilities balance — the editor raises a red cross while the
    // headless path, inheriting the raw 117.60, stayed balanced and published a
    // false `Success*`. Replay the SAME Role-9 recompute here (no visibility
    // context needed; calculateTotals_210100 covers all four columns, exactly as
    // the container calls it) so the inherited operands match the editor's. This
    // mirrors the recomputeReferencedSheets() step checkCrossSheetEquityByYear
    // already runs for the Role-19 (SOCIE) reference.
    const rawRole9Data = reader.read(
      SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
    );
    const xbrlPrefix = accountingStandardToXbrlPrefix(
      context.accountingStandard
    );
    const role9Data = rawRole9Data
      ? buildEqualitySnapshot(
          rawRole9Data,
          calculator.calculateTotals_210100(rawRole9Data, xbrlPrefix)
        )
      : rawRole9Data;
    const inheritedLeaves = applyLinkedInheritance(
      schema.controls,
      sheetData,
      role9Data,
      ROLE9_SUBCLASSIFICATION_LINK_ROLE
    );
    const equalityRecomputed = calculator.calculateTotals_210000(
      inheritedLeaves,
      xbrlPrefix
    );
    const equalitySnapshot = buildEqualitySnapshot(
      inheritedLeaves,
      equalityRecomputed
    );

    // GLOBAL RULE (PO, 2026-06-19, REVISED) — BLOCKING equality compares only
    // live data, NEVER the N8N snapshot.
    //
    // The blocking equality / equity-by-year layers run against `equalitySnapshot`
    // — the recompute of the CURRENT (Role-9-inherited) leaves (recompute-of-
    // latest). The N8N extracted snapshot is NOT an operand: per the PO rule the
    // snapshot is only ever compared against the recompute/latest of the SAME
    // field, and that is the non-blocking `Validated*` Suggested Change surfaced
    // further below (`calcDiffRecomputed` vs the snapshot).
    //
    // An earlier revision anchored these operands to the snapshot (recompute the
    // baseline's own leaves, then let the extracted totals win). That let an
    // INTERNALLY-INCONSISTENT extraction — a stored total disagreeing with its own
    // leaves — drive a false Assets == EquityAndLiabilities block, because the
    // stale stored total was compared against a freshly-recomputed sibling field.
    // The editor never does that: it recomputes from the leaves before checking.
    // Comparing recompute-of-latest vs recompute-of-latest mirrors the editor and
    // still blocks a GENUINE imbalance (recomputed totals that disagree).
    // Mirrors the Role-12 fix.
    // -----------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // Diagnostic logs — kept while we reconfirm that the extraction-vs-calc gap
    // (e.g. ifrs-full:Assets persisted vs recomputed) lands in hasSuggestedChanges
    // and NOT hasErrors. Remove once the values are confirmed across a sample.
    // console.log('[On Role 8 Financial Position | Validation]: Equality Errors: ', checkEqualityErrors(schema, equalitySnapshot, reader, visibleColumns, contextTaxonomy));
    // console.log('[On Role 8 Financial Position | Validation]: Equality Params (Schema): ', schema);
    // console.log('[On Role 8 Financial Position | Validation]: Equality Params (Snapshot): ', equalitySnapshot);
    // console.log('[On Role 8 Financial Position | Validation]: Equality Params (Columns): ', visibleColumns);
    // console.log('[On Role 8 Financial Position | Validation]: Cross Sheet Equity Errors: ', checkCrossSheetEquityByYear(equalitySnapshot, reader, context, visibleColumns));
    // -----------------
    // Layer 2 + 3 only fire when taxonomies agree (matches editor's
    // strict-mode short-circuit in validateAllEqualityRules).
    if (taxonomiesAgree) {
      if (checkEqualityErrors(schema, equalitySnapshot, reader, visibleColumns, contextTaxonomy)) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }

      // AS-1076: single reconciliation covering both Current Year (ungated) and
      // Previous Year (consecutive-gated) — see checkCrossSheetEquityByYear.
      if (checkCrossSheetEquityByYear(equalitySnapshot, reader, context, visibleColumns)) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }
    }

    // AS-842 calc-diff: with no errors found above, surface the editor's
    // `Validated*` signal in the silent path by diffing this sheet's recomputed
    // calculated totals against the immutable N8N extraction. Any mismatch flips
    // the sheet's `hasSuggestedChanges`, which the resolver rolls up into a
    // `Success*` conversion-level chip.
    //
    // The COMPARISON TARGET stays the raw extraction — `extractedBaseline`
    // (immutable N8N snapshot) when present, else the persisted slice — because
    // the asterisk's job is "recomputed sum vs extracted", which only exists
    // relative to the raw extraction.
    //
    // The RECOMPUTE SOURCE must mirror the mounted editor's
    // `validateAgainstApiData`, which runs AFTER `pullRole9Values()` — i.e. on
    // the Role-9-INHERITED leaves, not Role-8's raw persisted slice. At
    // conversion load the persisted slice can hold a Role-9-linked leaf that is
    // stale relative to Role-9's recompute (the editor reconciles it only on
    // mount), so recomputing the asterisk from that raw slice raised a transient
    // `Success*` on the sheet-selector dropdown that vanished the instant SOFP
    // was opened — with no parity warning, because parity-check only ever
    // compares same-instant inputs. Reusing `equalityRecomputed` (recompute from
    // the inherited leaves, already built above) makes the load-time verdict
    // match what the user sees on open.
    //
    // BUT only when we have the snapshot to diff against: without it (the
    // headless listing never loads the N8N snapshot) the target falls back to
    // the persisted slice, so the source must fall back with it — diffing
    // inherited totals against the stale persisted slice would MANUFACTURE a
    // false asterisk on the listing. Source and target flip together.
    const calcDiffRecomputed = context.extractedBaseline
      ? equalityRecomputed
      : calculator.calculateTotals_210000(sheetData, xbrlPrefix);
    return {
      hasErrors: false,
      // Guard: suppress the calc-diff asterisk while the taxonomy is unresolved
      // (empty accountingStandard defaults to the MFRS prefix → false `Success *`
      // on MPERS data). See isResolvedAccountingStandard.
      hasSuggestedChanges:
        isResolvedAccountingStandard(context.accountingStandard) &&
        hasCalculatedFieldMismatch(context.extractedBaseline ?? sheetData, calcDiffRecomputed),
    };
  },
};

